'use client';

import type { ReactNode } from 'react';

import { CategoryFilterDrawer } from '@eliadmin/core/src/components/dlz/category/filter-drawer';
import { FilterChips } from '@eliadmin/core/src/components/dlz/category/listing/filter-chips';
import { SortTabs } from '@eliadmin/core/src/components/dlz/category/listing/sort-tabs';
import {
  ListingRangeProvider,
  useListingRange,
  type ListingRange,
} from '@eliadmin/core/src/components/dlz/category/listing-context';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const NUMBER = new Intl.NumberFormat('sk-SK');

function countText({ from, to, total }: ListingRange) {
  const shown = from === to ? NUMBER.format(to) : `${NUMBER.format(from)}–${NUMBER.format(to)}`;
  return `Zobrazujem ${shown} z ${NUMBER.format(total)} ${total === 1 ? 'produktu' : 'produktov'}`;
}

/** „Zobrazujem 1–12 z 386 produktov“ – a live region, so loading more or filtering is announced. */
function ResultCount({ className }: { className?: string }) {
  const range = useListingRange();
  return (
    <p aria-live="polite" className={cn('dlz-body-s min-w-0 text-[#6B7079]', className)}>
      {range?.total ? countText(range) : null}
    </p>
  );
}

/**
 * Figma „Kategória · Výpis produktov“ 60091:1476: toolbar (filter drawer trigger, result count,
 * sort tabs), active filters, then the products (`CategoryProducts` reports its visible range).
 * One row from 1024 px; on tablets the sort tabs get their own row and scroll sideways to the
 * screen edges. Phones (60438:85567) stack everything 24 px apart in another order – the
 * full-width „Filtrovať“, the active filters, the centred count, the sort tabs: the toolbar
 * wrappers dissolve (`contents`) and the trigger and the chips move to the front.
 */
export function CategoryListing({ children }: { children: ReactNode }) {
  const { t } = useTranslations();

  return (
    <ListingRangeProvider>
      <section
        data-section="category-listing"
        aria-label={t('text150')}
        className="pb-12 pt-6 lg:pb-20"
      >
        <Container className="flex flex-col gap-6">
          <h2 className="sr-only">{t('text150')}</h2>
          <div className="max-md:contents md:flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
            <div className="flex min-w-0 items-center gap-4 max-md:contents md:gap-6">
              <CategoryFilterDrawer className="max-md:-order-2" />
              <ResultCount className="max-md:text-center" />
            </div>
            <SortTabs className="md:-mx-6 md:px-6 lg:mx-0 lg:shrink-0 lg:px-0" />
          </div>
          <FilterChips className="max-md:-order-1" />
          {children}
        </Container>
      </section>
    </ListingRangeProvider>
  );
}
